import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GraduationCap, ArrowRight, ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { setPath } from '../lib/setPath';
import { stripHtml } from '../lib/richText';
import { EditorStepper } from '../components/course-editor/EditorStepper';
import { BasicInfoCard } from '../components/course-editor/BasicInfoCard';
import { LearnPointsCard } from '../components/course-editor/LearnPointsCard';
import { ExtraInfoCard } from '../components/course-editor/ExtraInfoCard';
import { CoverCard, IntroVideoCard } from '../components/course-editor/MediaCards';
import { StatusCard } from '../components/course-editor/StatusCard';
import { ReviewStep } from '../components/course-editor/ReviewStep';
import { ContentManager } from '../components/course-editor/content/ContentManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith('/api/') ? `${BACKEND_URL}${u}` : u || '');
const TEXT_KEYS = ['titleAr', 'titleEn', 'brandAr', 'brandEn', 'subtitleAr', 'subtitleEn', 'levelAr', 'levelEn', 'descriptionAr', 'descriptionEn', 'color', 'youtube', 'language', 'cover', 'video_url'];

const emptyCourse = () => ({
  id: '', titleAr: '', titleEn: '', brandAr: '', brandEn: '', subtitleAr: '', subtitleEn: '', levelAr: '', levelEn: '',
  descriptionAr: '', descriptionEn: '', hours: 0, color: 'blue', youtube: '', language: 'ar', cover: '', video_url: '',
  categories: ['general'], is_free: false, published: true, featured: false, show_home: true, learnAr: ['', '', ''], learnEn: ['', '', ''],
  image: null, imagePreview: '', videoFile: null, videoFileName: '',
});

const fromApi = (c) => ({
  ...emptyCourse(), ...c, cover: abs(c.cover), video_url: abs(c.video_url),
  published: c.published !== false, show_home: c.show_home !== false, featured: !!c.featured,
  learnAr: c.learnAr || [], learnEn: c.learnEn || [], categories: c.categories?.length ? c.categories : ['general'],
});

const toFormData = (form, overrides) => {
  const f = { ...form, ...overrides };
  const fd = new FormData();
  TEXT_KEYS.forEach(k => fd.append(k, f[k] || ''));
  fd.append('hours', String(f.hours || 0));
  fd.append('students', String(f.students || 0));
  fd.append('categories', f.categories.join(','));
  const pts = f.learnAr.map((ar, i) => [ar.trim(), (f.learnEn[i] || '').trim()]).filter(([ar]) => ar);
  fd.append('learnAr', JSON.stringify(pts.map(p => p[0])));
  fd.append('learnEn', JSON.stringify(pts.map(p => p[1])));
  ['is_free', 'published', 'featured', 'show_home'].forEach(k => fd.append(k, f[k] ? 'true' : 'false'));
  if (f.image) fd.append('image', f.image);
  if (f.videoFile) fd.append('video', f.videoFile);
  return fd;
};

export default function CourseEditor() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { lang, isRTL } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState(emptyCourse);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(!!id);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const step = params.get('step') || 'basic';
  const set = (path, value) => setForm(f => setPath(f, path, value));
  const Back = isRTL ? ArrowRight : ArrowLeft;
  const Crumb = isRTL ? ChevronLeft : ChevronRight;

  useEffect(() => { if (!authLoading && (!user || user.role !== 'admin')) navigate('/login'); }, [user, authLoading, navigate]);
  useEffect(() => {
    if (!id) return;
    api.get('/courses').then(({ data }) => {
      const c = (data || []).find(x => x.id === id);
      if (!c) { toast.error(L('الكورس غير موجود', 'Course not found')); navigate('/admin'); return; }
      setForm(fromApi(c)); setLessons(c.lessons || []);
    }).finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (overrides = {}) => {
    if (!form.titleAr.trim() || !stripHtml(form.descriptionAr).trim()) {
      toast.error(L('أكمل الحقول المطلوبة: الاسم والوصف بالعربية', 'Fill required fields: Arabic name and description'));
      return null;
    }
    setBusy(true); setProgress(0);
    try {
      const cfg = { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)) };
      const fd = toFormData(form, overrides);
      const { data } = form.id ? await api.patch(`/courses/${form.id}`, fd, cfg) : await api.post('/courses', fd, cfg);
      setForm(fromApi(data)); setLessons(data.lessons || []);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || L('تعذر الحفظ', 'Could not save'));
      return null;
    } finally { setBusy(false); setProgress(0); }
  };

  const goStep = (s, courseId = form.id) => { navigate(`/admin/courses/${courseId}?step=${s}`, { replace: !!id }); window.scrollTo({ top: 0 }); };
  const onContinue = async () => { const d = await save(); if (d) { toast.success(L('تم حفظ الكورس', 'Course saved')); goStep('content', d.id); } };
  const onDraft = async () => { const d = await save({ published: false }); if (d) { toast.success(L('تم الحفظ كمسودة', 'Saved as draft')); if (!id) navigate(`/admin/courses/${d.id}`, { replace: true }); } };
  const onPublish = async () => { const d = await save({ published: true }); if (d) { toast.success(L('تم نشر الكورس', 'Course published')); navigate('/admin?tab=courses'); } };

  if (loading) return <main className="pt-28 min-h-screen flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></main>;

  return (
    <main data-testid="course-editor-page" className="page-y min-h-screen">
      <div className="site-container">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
          <Link to="/admin?tab=courses" data-testid="course-editor-back" className="p-1.5 rounded-lg hover:bg-slate-100"><Back className="w-4 h-4" /></Link>
          <Link to="/admin?tab=courses" className="hover:text-blue-600">{L('الكورسات', 'Courses')}</Link>
          <Crumb className="w-3.5 h-3.5" />
          <span className="text-slate-800 font-bold">{form.id ? L('تعديل الكورس', 'Edit course') : L('إضافة كورس جديد', 'Add new course')}</span>
        </div>
        <div className="flex items-center gap-4 mb-6">
          <span className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center"><GraduationCap className="w-7 h-7" /></span>
          <div>
            <h1 data-testid="course-editor-title" className="text-2xl sm:text-3xl font-black text-slate-900">{form.id ? (form.titleAr || L('تعديل الكورس', 'Edit course')) : L('إضافة كورس جديد', 'Add new course')}</h1>
            <p className="text-sm text-slate-500 mt-0.5">{L('أنشئ كورساً جديداً وأضف جميع المعلومات والتفاصيل بسهولة', 'Create a course and add all its details easily')}</p>
          </div>
        </div>
        <EditorStepper step={step} onStep={(s) => goStep(s)} canAdvance={!!form.id} />

        {step === 'basic' && (
          <div className="grid lg:grid-cols-[minmax(0,1fr)_440px] gap-6 items-start">
            <div className="space-y-6 min-w-0">
              <BasicInfoCard form={form} set={set} />
              <div className="grid xl:grid-cols-2 gap-6 items-start">
                <LearnPointsCard form={form} setForm={setForm} set={set} />
                <ExtraInfoCard form={form} set={set} />
              </div>
            </div>
            <aside className="space-y-6 lg:sticky lg:top-24">
              <CoverCard form={form} setForm={setForm} />
              <IntroVideoCard form={form} setForm={setForm} set={set} />
              <StatusCard form={form} set={set} onContinue={onContinue} onDraft={onDraft} busy={busy} progress={progress} />
            </aside>
          </div>
        )}

        {step === 'content' && form.id && (
          <section data-testid="course-content-step" className="rounded-2xl bg-white border border-slate-200/80 shadow-sm p-4 sm:p-6">
            <ContentManager courseId={form.id} sections={form.sections || []} lessons={lessons} setLessons={setLessons}
              setSections={(secs) => setForm(f => ({ ...f, sections: secs }))} />
            <div className="flex justify-end mt-6">
              <button type="button" data-testid="course-to-review-btn" onClick={() => goStep('review')} className="h-11 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors">{L('التالي: مراجعة ونشر', 'Next: review & publish')}</button>
            </div>
          </section>
        )}

        {step === 'review' && form.id && <ReviewStep form={form} lessons={lessons} onPublish={onPublish} busy={busy} />}
      </div>
    </main>
  );
}
